import Box from '@mui/material/Box';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import type {
  CdnLocation,
  PulseCdnOptions,
} from '../../../../../../pulsecdn/types';
import {
  postCdnLocation,
  useMessageParameter,
  useOnMessage,
} from '../../../../../../pulsecdn/utils/messages';
import { getPulseCdnIframeUrl } from '../../../../../../pulsecdn/utils/path.ts';
import S3File from '../../../store/S3File';
import Navigation from '../Navigation';
import classes from './PulseCdnBrowser.module.css';
import useIframeSizeObserver from './useIframeSizeObserver';

const MIN_HEIGHT = 450;
const MAX_HEIGHT = 610;

export default observer(function PulseCdnBrowser({
  pulseCdnOptions,
  setNoCloseReason,
  setSelectedFiles,
  setUrl,
  showActionButtons,
  url,
}: {
  pulseCdnOptions?: PulseCdnOptions;
  setNoCloseReason: (reason: string) => void;
  setSelectedFiles: (files: S3File[]) => void;
  setUrl: (url: string) => void;
  showActionButtons: (state: boolean) => void;
  url: string;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const location = useMessageParameter<CdnLocation>('cdnOnLocationChange');
  const [iframeHeight, setIframeHeight] = useState<number>(MIN_HEIGHT);
  const [cdnDialogShown, setCdnDialogShown] = useState(false);

  useIframeSizeObserver(iframeRef, ({ height }) => {
    setIframeHeight(Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height + 10)));
  });

  useOnMessage<S3File[]>('cdnOnSelectFiles', (files) => {
    setSelectedFiles(files.map((file) => new S3File(file)));
  });

  useOnMessage<boolean>('cdnOnDialog', (shown) => {
    setCdnDialogShown(shown);
    if (shown) {
      setSelectedFiles([]);
    }
    showActionButtons(!shown);
  });

  useOnMessage<boolean>('cdnOnUpload', (inProgress) => {
    setNoCloseReason(
      inProgress ? 'Press Pause button to cancel uploading' : '',
    );
  });

  function onNavigate(newUrl: string) {
    try {
      const { hostname } = new URL(newUrl);
      if (!hostname.includes('pulsecdn.net')) {
        setUrl(newUrl);
        return;
      }
    } catch {
      return;
    }

    postCdnLocation(iframeRef.current, newUrl);
  }

  return (
    <Box className={classes.root}>
      {!cdnDialogShown && (
        <Navigation onNavigate={onNavigate} url={location?.url || url} />
      )}
      <iframe
        className={classes.frame}
        ref={iframeRef}
        src={getPulseCdnIframeUrl(getStartPath(url), {
          heightMode: 'content',
          showCreateMenu: false,
          showNewFolderButton: true,
          ...pulseCdnOptions,
        })}
        style={{ height: cdnDialogShown ? undefined : iframeHeight }}
        title="PulseCDN"
      />
    </Box>
  );
});

function getStartPath(url: string) {
  try {
    return new URL(url).pathname;
  } catch {
    return '';
  }
}
